// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import P from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import styles from './universal-background-image.module.less';

const UniversalBackgroundImage = ({
  children,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  parallaxContainerHeightDesktop,
  parallaxContainerHeightMobile,
  backgroundSize,
  backgroundPositionY,
  backgroundPositionX,
  size,
  useParallax,
  parallaxOpacity,
  backgroundAttachment,
}) => {
  const backgroundImageSrcMap = (screenSize) => {
    // return desktop background if mobile/tablet is not provided
    const type = {
      lg: backgroundImageSrcDesktop,
      md: backgroundImageSrcTablet,
      sm: backgroundImageSrcMobile,
    };
    if (type[screenSize]) {
      return type[screenSize];
    }
    return backgroundImageSrcDesktop;
  };

  const getContainerHeight = () =>
    size !== 'lg'
      ? parallaxContainerHeightMobile
      : parallaxContainerHeightDesktop;

  const parallaxBackground = useParallax
    ? {
        backgroundSize,
        boxShadow: parallaxOpacity,
        backgroundAttachment,
      }
    : {};

  return (
    <div
      className={useParallax ? styles.parallaxEffect : ''}
      style={{
        backgroundImage: `url(${backgroundImageSrcMap(size)})`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionY,
        backgroundPositionX,
        backgroundSize,
        height: useParallax ? getContainerHeight() || 'auto' : 'initial',
        ...parallaxBackground,
      }}
    >
      {children}
    </div>
  );
};

UniversalBackgroundImage.displayName = 'UniversalBackgroundImage';

UniversalBackgroundImage.propTypes = {
  children: P.node,
  backgroundImageSrcDesktop: P.string,
  backgroundImageSrcTablet: P.string,
  backgroundImageSrcMobile: P.string,
  useParallax: P.bool,
  size: P.string,
  backgroundSize: P.oneOf(['contain', 'cover', 'auto']),
  backgroundPositionX: P.oneOf(['left', 'right', 'center']),
  backgroundPositionY: P.oneOf(['top', 'bottom', 'center']),
  backgroundAttachment: P.oneOf(['fixed', 'scroll']),
  parallaxOpacity: P.string,
  parallaxContainerHeightDesktop: P.number,
  parallaxContainerHeightMobile: P.number,
};

UniversalBackgroundImage.defaultProps = {
  children: null,
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  useParallax: false,
  size: '',
  backgroundSize: 'cover',
  backgroundPositionX: 'left',
  backgroundPositionY: 'top',
  backgroundAttachment: 'fixed',
  parallaxOpacity: 'none',
  parallaxContainerHeightDesktop: null,
  parallaxContainerHeightMobile: null,
};

const isTestEnv = process?.env?.NODE_ENV;
/**
 * @param {*} componentDocument
 * @param {string} fieldLabel Field Label in the Editor
 * @param {Array<'parallax'|'backgroundSize'|'backgroundPosition'>} allowedControlsList List of controls to be shown in the editor
 * @param {string} fieldName
 * @returns
 */
UniversalBackgroundImage.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal Background Image',
  allowedControlsList = isTestEnv
    ? ['parallax', 'backgroundSize', 'backgroundPosition']
    : [],
  fieldName = 'universalBackgroundImage'
) => {
  const parallaxInputConfig =
    get(componentDocument, `props.${fieldName}.useParallax`, false) === true
      ? [
          stringSelectSpec(
            `${fieldName}.parallaxOpacity`,
            'Select Parallax Background Opacity. Defaults to "No Opacity"',
            [
              { label: 'No Opacity', value: 'none' },
              {
                label: 'Dark Opacity',
                value: 'rgb(10 10 10 / 50%) 0px 0px 0px 100vw inset',
              },
              {
                label: 'Light Opacity',
                value: 'rgb(255 255 255 / 50%) 0px 0px 0px 100vw inset',
              },
            ],
            {
              help: 'Apply opacity to the background',
            }
          ),
          numberInputSpec(
            `${fieldName}.parallaxContainerHeightDesktop`,
            'Container Height Desktop'
          ),
          numberInputSpec(
            `${fieldName}.parallaxContainerHeightMobile`,
            'Container Height Mobile'
          ),
        ]
      : [];

  const parallaxEditorControls = allowedControlsList.includes('parallax')
    ? [
        group('Parallax', [
          boolCheckboxSpec(`${fieldName}.useParallax`, 'Use Parallax Effect?', {
            help: 'Not available for IOS',
          }),
          ...parallaxInputConfig,
        ]),
      ]
    : [];

  const backgroundSizeEditorControls = allowedControlsList.includes(
    'backgroundSize'
  )
    ? [
        group('Background Size', [
          stringSelectSpec(
            `${fieldName}.backgroundSize`,
            'Background Size',
            [
              { label: 'Cover', value: 'cover' },
              { label: 'Auto', value: 'auto' },
              { label: 'Contain', value: 'contain' },
            ],
            {
              help: 'Only applies to Desktop and Tablet',
            }
          ),
        ]),
      ]
    : [];

  const backgroundPositionEditorControls = allowedControlsList.includes(
    'backgroundPosition'
  )
    ? [
        group('Background Position', [
          stringSelectSpec(
            `${fieldName}.backgroundPositionY`,
            'Background Vertical Position',
            [
              { label: 'Top', value: 'top' },
              { label: 'Center', value: 'center' },
              { label: 'Bottom', value: 'bottom' },
            ],
            {
              help: 'Defaults to "top"',
            }
          ),
          stringSelectSpec(
            `${fieldName}.backgroundPositionX`,
            'Background Horizontal Position',
            [
              { label: 'Left', value: 'left' },
              { label: 'Center', value: 'center' },
              { label: 'Right', value: 'right' },
            ],
            {
              help: 'Defaults to "left"',
            }
          ),
        ]),
      ]
    : [];

  return [
    group(fieldLabel, [
      stringInputSpec(
        `${fieldName}.backgroundImageSrcDesktop`,
        'Background Image URL (Desktop)'
      ),
      stringInputSpec(
        `${fieldName}.backgroundImageSrcTablet`,
        'Background Image URL (Tablet)'
      ),
      stringInputSpec(
        `${fieldName}.backgroundImageSrcMobile`,
        'Background Image URL (Mobile)'
      ),
      ...parallaxEditorControls,
      ...backgroundSizeEditorControls,
      ...backgroundPositionEditorControls,
    ]),
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(UniversalBackgroundImage);
